const AUTO_FETCH_DEBOUNCE_MS = 400
const SIMPLE_TABLE_TYPES = ['custody_panel', 'servicer_panel', 'reconciliation_panel', 'rules_panel', 'split_block', 'pricing_panel']

function hasEagerDrawer(item) {
  return Boolean(item && item.detail && Array.isArray(item.detail.children))
}

export function withoutEmptyValues(values) {
  return Object.keys(values || {}).reduce((result, key) => {
    if (values[key] !== '') {
      result[key] = values[key]
    }
    return result
  }, {})
}

export function hasDetail(item) {
  return Boolean(item && item.detail && typeof item.detail === 'object')
}

export function drawerRowListKeys(props, predicate = hasEagerDrawer) {
  if (!props || typeof props !== 'object') {
    return []
  }
  return Object.keys(props).filter((key) => Array.isArray(props[key]) && props[key].some(predicate))
}

export function eachEagerDrawerChildren(props, callback) {
  drawerRowListKeys(props).forEach((key) => {
    props[key].forEach((row) => {
      if (hasEagerDrawer(row)) {
        callback(row.detail.children)
      }
    })
  })
  if (props && hasEagerDrawer(props)) {
    callback(props.detail.children)
  }
}

function emittedSort(options) {
  if (!Array.isArray(options.sortBy)) {
    return {}
  }
  const sortBy = options.sortBy[0] || ''
  return { sortBy, sortDesc: Boolean(sortBy && Array.isArray(options.sortDesc) && options.sortDesc[0]) }
}

export default {
  data() {
    return {
      tableFilterValues: {},
      tableSearchValues: {},
      tablePages: {},
      tableItemsPerPage: {},
      tableSortBy: {},
      tableSortDesc: {},
      referenceDateValues: {}
    }
  },
  watch: {
    baseScreen: {
      immediate: true,
      handler(node) {
        this.seedTableFilterValues(node)
      }
    }
  },
  computed: {
    tableQueryParams() {
      const result = {}
      Object.keys(this.tableFilterValues).forEach((instance) => {
        const values = this.tableFilterValues[instance] || {}
        Object.keys(values).forEach((param) => {
          const value = values[param]
          if (value && typeof value === 'object' && !Array.isArray(value)) {
            if (value.start !== undefined) result[`${param}.start`] = value.start
            if (value.end !== undefined) result[`${param}.end`] = value.end
          } else {
            result[param] = value
          }
        })
      })
      Object.keys(this.tableSearchValues).forEach((instance) => {
        const value = this.tableSearchValues[instance]
        if (value) {
          result[`searchPlaceholder.${instance}`] = value
        }
      })
      Object.keys(this.tablePages).forEach((instance) => {
        result[`page.${instance}`] = this.tablePages[instance]
      })
      Object.keys(this.tableItemsPerPage).forEach((instance) => {
        result[`size.${instance}`] = this.tableItemsPerPage[instance]
      })
      Object.keys(this.tableSortBy).forEach((instance) => {
        const sortBy = this.tableSortBy[instance]
        if (sortBy) {
          result[`sort.${instance}`] = sortBy
          result[`sortDesc.${instance}`] = Boolean(this.tableSortDesc[instance])
        }
      })
      Object.keys(this.referenceDateValues).forEach((instance) => {
        const value = this.referenceDateValues[instance]
        if (value) {
          result[`referenceDate.${instance}`] = value
        }
      })
      return result
    }
  },
  methods: {
    onFilterChange({ instance, param, value, autoFetch } = {}) {
      if (!instance || !param) {
        return
      }
      this.dropUndeclaredTableFilters(instance)
      this.setTableFilterValue(instance, param, value)
      if (autoFetch) {
        this.debouncedRefetch(instance)
      }
    },
    setTableFilterValue(instance, param, value) {
      const current = { ...(this.tableFilterValues[instance] || {}) }
      current[param] = value
      this.$set(this.tableFilterValues, instance, current)
      this.$delete(this.tablePages, instance)
    },
    dropUndeclaredTableFilters(instance) {
      const declared = (this._declaredTableFilterParams || {})[instance]
      const current = this.tableFilterValues[instance]
      if (!declared || !current) {
        return
      }
      const kept = Object.keys(current).filter((param) => declared.includes(param))
      if (kept.length === Object.keys(current).length) {
        return
      }
      this.$set(this.tableFilterValues, instance, kept.reduce((result, param) => {
        result[param] = current[param]
        return result
      }, {}))
    },
    debouncedRefetch(instance) {
      if (typeof this.performFetch !== 'function') {
        return
      }
      clearTimeout(this._autoFetchTimer)
      this._autoFetchTimer = setTimeout(() => {
        this.performFetch(instance)
      }, AUTO_FETCH_DEBOUNCE_MS)
    },
    onReferenceDateChange({ instance, value } = {}) {
      if (!instance) {
        return
      }
      const changed = this.referenceDateValues[instance] !== value
      this.$set(this.referenceDateValues, instance, value)
      if (changed && typeof this.performFetch === 'function') {
        this.performFetch(instance)
      }
    },
    applyReferenceDateState(node) {
      if (node.type !== 'pricing_panel' || !node.props) {
        return null
      }
      const instance = node.props.instance
      if (!instance || this.referenceDateValues[instance] === undefined) {
        return null
      }
      return { ...node.props, referenceDate: this.referenceDateValues[instance] }
    },
    applyNodeQueryState(node, transformNode) {
      const optionsProps = this.applyTableQueryState(node, transformNode)
      const dateProps = this.applyReferenceDateState(node)
      if (optionsProps && dateProps) {
        return { ...optionsProps, referenceDate: dateProps.referenceDate }
      }
      return optionsProps || dateProps
    },
    onTableOptionsChange({ instance, options } = {}) {
      if (!instance || !options) {
        return
      }
      const pageChanged = this.tablePages[instance] !== options.page || this.tableItemsPerPage[instance] !== options.itemsPerPage
      if (!this._lastTableOptions) {
        this._lastTableOptions = {}
      }
      const emitted = { page: options.page, itemsPerPage: options.itemsPerPage, ...emittedSort(options) }
      const lastEmitted = this._lastTableOptions[instance] || emitted
      const previous = {
        page: this.tablePages[instance] !== undefined ? this.tablePages[instance] : lastEmitted.page,
        itemsPerPage: this.tableItemsPerPage[instance] !== undefined ? this.tableItemsPerPage[instance] : lastEmitted.itemsPerPage,
        sortBy: this.tableSortBy[instance] !== undefined ? this.tableSortBy[instance] : lastEmitted.sortBy,
        sortDesc: this.tableSortDesc[instance] !== undefined ? this.tableSortDesc[instance] : lastEmitted.sortDesc
      }
      this._lastTableOptions[instance] = emitted
      const sortChanged = this.recordTableSort(instance, options)
      this.$set(this.tablePages, instance, options.page)
      this.$set(this.tableItemsPerPage, instance, options.itemsPerPage)
      if (pageChanged || sortChanged) {
        this.rememberTableOptions(instance, previous)
        this.scheduleTableOptionsFetch(instance)
      }
    },
    recordTableSort(instance, options) {
      const { sortBy, sortDesc } = emittedSort(options)
      if (sortBy === undefined) {
        return false
      }
      const changed = (this.tableSortBy[instance] || '') !== sortBy || Boolean(this.tableSortDesc[instance]) !== sortDesc
      this.$set(this.tableSortBy, instance, sortBy)
      this.$set(this.tableSortDesc, instance, sortDesc)
      return changed
    },
    rememberTableOptions(instance, previous) {
      if (typeof this.performFetch !== 'function') {
        return
      }
      if (!this._tableOptionsSnapshots) {
        this._tableOptionsSnapshots = {}
      }
      if (!(instance in this._tableOptionsSnapshots)) {
        this._tableOptionsSnapshots[instance] = previous
      }
    },
    startTableQueryRequest() {
      this._tableQueryRequestId = (this._tableQueryRequestId || 0) + 1
      return this._tableQueryRequestId
    },
    isLatestTableQueryRequest(requestId) {
      return requestId === this._tableQueryRequestId
    },
    confirmTableOptions(requestId) {
      if (!this.isLatestTableQueryRequest(requestId)) {
        return
      }
      this._tableOptionsSnapshots = {}
    },
    rollbackTableOptions(requestId) {
      if (!this.isLatestTableQueryRequest(requestId)) {
        return
      }
      const snapshots = this._tableOptionsSnapshots || {}
      this._tableOptionsSnapshots = {}
      Object.keys(snapshots).forEach((instance) => {
        this.restoreTableOption(this.tablePages, instance, snapshots[instance].page)
        this.restoreTableOption(this.tableItemsPerPage, instance, snapshots[instance].itemsPerPage)
        this.restoreTableOption(this.tableSortBy, instance, snapshots[instance].sortBy)
        this.restoreTableOption(this.tableSortDesc, instance, snapshots[instance].sortDesc)
      })
    },
    restoreTableOption(map, instance, value) {
      if (value === undefined) {
        this.$delete(map, instance)
      } else {
        this.$set(map, instance, value)
      }
    },
    scheduleTableOptionsFetch(instance) {
      if (typeof this.performFetch !== 'function') {
        return
      }
      this._pendingOptionsFetchInstance = instance
      if (this._optionsFetchTimer) {
        return
      }
      this._optionsFetchTimer = setTimeout(() => {
        this._optionsFetchTimer = null
        const pendingInstance = this._pendingOptionsFetchInstance
        this._pendingOptionsFetchInstance = null
        this.performFetch(pendingInstance)
      }, 0)
    },
    onFilterSubmit({ instance, value } = {}) {
      if (instance) {
        this.dropUndeclaredTableFilters(instance)
        if (value) {
          this.$set(this.tableSearchValues, instance, value)
        } else {
          this.$delete(this.tableSearchValues, instance)
        }
        this.$delete(this.tablePages, instance)
      }
      if (typeof this.performFetch === 'function') {
        this.performFetch(instance)
      }
    },
    tableOptionsOverride(instance) {
      if (!instance) {
        return null
      }
      const pageOverride = this.tablePages[instance]
      const itemsPerPageOverride = this.tableItemsPerPage[instance]
      const sortByOverride = this.tableSortBy[instance]
      if (pageOverride === undefined && itemsPerPageOverride === undefined && sortByOverride === undefined) {
        return null
      }
      const sortOverride = sortByOverride
        ? { sortBy: [sortByOverride], sortDesc: [Boolean(this.tableSortDesc[instance])] }
        : { sortBy: [], sortDesc: [] }
      return {
        ...(pageOverride !== undefined ? { page: pageOverride } : {}),
        ...(itemsPerPageOverride !== undefined ? { itemsPerPage: itemsPerPageOverride } : {}),
        ...(sortByOverride !== undefined ? sortOverride : {})
      }
    },
    applySimpleTableOptionsState(node) {
      if (!node.props) {
        return null
      }
      const override = this.tableOptionsOverride(node.props.instance)
      if (!override) {
        return null
      }
      return { ...node.props, options: { ...(node.props.options || {}), ...override } }
    },
    applyReportingRegistryQueryState(node) {
      if (!node.props) {
        return null
      }
      const overrideFor = (instance, optionsKey) => {
        const override = this.tableOptionsOverride(instance)
        return override ? { [optionsKey]: { ...(node.props[optionsKey] || {}), ...override } } : null
      }
      const instance = node.props.instance
      const issuedOverride = overrideFor(instance ? `${instance}-issued` : '', 'issuedOptions')
      const scheduledOverride = overrideFor(instance ? `${instance}-scheduled` : '', 'scheduledOptions')
      if (!issuedOverride && !scheduledOverride) {
        return null
      }
      return { ...node.props, ...issuedOverride, ...scheduledOverride }
    },
    applyTableQueryState(node, transformNode) {
      const ownProps = this.applyOwnTableState(node)
      return this.applyEagerDrawersState(ownProps || node.props, transformNode) || ownProps
    },
    applyOwnTableState(node) {
      if (SIMPLE_TABLE_TYPES.includes(node.type)) {
        return this.applySimpleTableOptionsState(node)
      }
      if (node.type === 'reporting_registry') {
        return this.applyReportingRegistryQueryState(node)
      }
      const isDataTableFilter = node.type === 'data_table_filter'
      const isFilterBar = node.type === 'filter_bar'
      if (!(isDataTableFilter || isFilterBar) || !node.props || !node.props.instance) {
        return null
      }
      const instance = node.props.instance
      const props = {
        ...node.props,
        filterValues: this.tableFilterValues[instance] || {},
        search: this.tableSearchValues[instance] || ''
      }
      if (isDataTableFilter) {
        props.options = { ...(node.props.options || {}), ...(this.tableOptionsOverride(instance) || {}) }
      }
      return props
    },
    applyEagerDrawersState(props, transformNode) {
      if (!props || typeof transformNode !== 'function') {
        return null
      }
      const keys = drawerRowListKeys(props)
      const hasOwnDrawer = hasEagerDrawer(props)
      if (!keys.length && !hasOwnDrawer) {
        return null
      }
      const withTransformedDrawer = (item) => (hasEagerDrawer(item)
        ? { ...item, detail: { ...item.detail, children: item.detail.children.map(transformNode) } }
        : item)
      const result = hasOwnDrawer ? withTransformedDrawer(props) : { ...props }
      keys.forEach((key) => {
        result[key] = props[key].map(withTransformedDrawer)
      })
      return result
    },
    seedTableFilterValues(node) {
      if (!node || typeof node !== 'object') {
        return
      }
      if ((node.type === 'data_table_filter' || node.type === 'filter_bar') && node.props) {
        const instance = node.props.instance
        if (instance && this.tableFilterValues[instance] === undefined && node.props.filterValues) {
          this.$set(this.tableFilterValues, instance, node.props.filterValues)
        }
        if (instance) {
          const filters = Array.isArray(node.props.filters) ? node.props.filters : []
          const declared = this._declaredTableFilterParams || {}
          this._declaredTableFilterParams = {
            ...declared,
            [instance]: [...new Set([
              ...(declared[instance] || []),
              ...filters.map((filter) => filter && filter.param).filter(Boolean),
              ...Object.keys(node.props.filterValues || {})
            ])]
          }
        }
      }
      eachEagerDrawerChildren(node.props, (children) => {
        children.forEach((child) => this.seedTableFilterValues(child))
      })
      if (Array.isArray(node.sections)) {
        node.sections.forEach((child) => this.seedTableFilterValues(child))
      }
      if (Array.isArray(node.children)) {
        node.children.forEach((child) => this.seedTableFilterValues(child))
      }
      if (node.type === 'accordion_drilldown' && node.props && Array.isArray(node.props.items)) {
        node.props.items.forEach((item) => {
          if (Array.isArray(item.tabs)) {
            item.tabs.forEach((tab) => {
              if (Array.isArray(tab.children)) {
                tab.children.forEach((child) => this.seedTableFilterValues(child))
              }
            })
          }
        })
      }
    }
  },
  beforeDestroy() {
    clearTimeout(this._autoFetchTimer)
    clearTimeout(this._optionsFetchTimer)
  }
}
